import { rem } from '@mantine/core';
import { useState } from 'react';
import { usePostTagsMutation, type PostRow } from '../api/posts';
import { useSession } from '../api/session';
import type { TagRow } from '../api/tags';
import { TagChip, TagPicker } from '../volunteers/TagBits';

/**
 * Labels of a post, for finding and grouping posts on this screen. They are the
 * coordinators' own: nothing of them reaches the briefing.
 *
 * Like photos, these write immediately rather than waiting for Shrani, and the
 * write leaves the post's `updatedAt` alone — so toggling a label never throws
 * away what is typed in the form around it.
 */
export function PostTagCard({ post, tags }: { post: PostRow; tags: TagRow[] }) {
  const mut = usePostTagsMutation();
  // Labelling calls both /posts/tags and the tag vocabulary (SPEC §1.1L).
  const { can } = useSession();
  const canEdit = can('plan:write') && can('people:write');
  const [open, setOpen] = useState(false);
  const own = tags.filter((t) => post.tagIds.includes(t.id));

  return (
    <div
      style={{
        position: 'relative',
        background: 'oklch(1 0 0)',
        border: '1px solid oklch(0.92 0.006 95)',
        borderRadius: 11,
        padding: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
        <span style={{ fontSize: rem(12), letterSpacing: '0.04em', color: 'oklch(0.55 0.012 90)' }}>
          OZNAKE
        </span>
        <span style={{ fontSize: rem(12), color: 'oklch(0.62 0.012 90)' }}>
          — skupne vidijo vsi, osebne ◍ samo ti
        </span>
      </div>
      {canEdit ? (
        <div style={{ fontSize: rem(12), color: 'oklch(0.6 0.012 90)', lineHeight: 1.4, marginBottom: 10 }}>
          Shranijo se takoj, brez gumba Shrani. Redar jih ne vidi.
        </div>
      ) : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
        {own.map((t) => (
          <TagChip
            key={t.id}
            tag={t}
            onRemove={canEdit ? () => mut.mutate({ postIds: [post.id], remove: [t.id] }) : undefined}
          />
        ))}
        {own.length === 0 ? (
          <span style={{ fontSize: rem(12.5), color: 'oklch(0.62 0.012 90)' }}>Brez oznak.</span>
        ) : null}
        {canEdit ? (
          <button
            onClick={() => setOpen((o) => !o)}
            style={{
              cursor: 'pointer',
              padding: '4px 11px',
              borderRadius: 20,
              fontSize: rem(12.5),
              fontFamily: 'inherit',
              background: 'oklch(0.985 0.004 95)',
              color: 'oklch(0.42 0.012 90)',
              border: '1px dashed oklch(0.85 0.008 95)',
            }}
          >
            + Oznaka
          </button>
        ) : null}
      </div>
      {open ? (
        <TagPicker
          kind="post"
          tags={tags}
          targets={[post]}
          onApply={(delta) => mut.mutate({ postIds: [post.id], ...delta })}
          onClose={() => setOpen(false)}
          style={{ position: 'absolute', left: 16, right: 16, top: '100%', marginTop: -6 }}
        />
      ) : null}
    </div>
  );
}
